import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import {
  getAllDeleteRequest,
  approveDeleteRequest,
  rejectDeleteRequest,
} from "../../service/orderService";

const OrderApproval = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const statusColors = {
    request_for_delete: { backgroundColor: "#FFA500", color: "#000000" },
  };

  /** Fetch pending delete requests */

  const fetchPendingDeleteRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllDeleteRequest({ limit: 0 });
      const data = res?.data?.data.data;
      // console.log("res", res);
      // console.log("res2", data);

      if (Array.isArray(data)) {
        const normalizedRows = data.map((row) => ({
          ...row,
          customerName: row.customer?.name || "",
          branchName: row.branch?.name || "",
          order_date: row.order_date ? row.order_date.split("T")[0] : "",
        }));
        setRows(normalizedRows);
      } else {
        setRows([]);
        showSnackbar(
          "Unexpected data format for pending delete requests!",
          "error"
        );
      }
    } catch (error) {
      console.error("Fetch pending delete requests error:", error);
      showSnackbar(
        error?.response?.data?.message ||
          "Failed to fetch pending delete requests!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchPendingDeleteRequests();
  }, [fetchPendingDeleteRequests]);

  /** Approve delete request */
  const handleApproveDelete = async (id) => {
    try {
      const response = await approveDeleteRequest(id);
      showSnackbar(
        response?.message || "Order delete request approved successfully!",
        "success"
      );
      fetchPendingDeleteRequests();
    } catch (error) {
      console.error("Approve Delete Error:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to approve delete request!",
        "error"
      );
    } finally {
      setApproveDialogOpen(false);
      setDeleteId(null);
      setSelectedRow(null);
    }
  };

  /** Reject delete request */
  const handleRejectDelete = async (id) => {
    try {
      const response = await rejectDeleteRequest(id, rejectionReason);
      showSnackbar(
        response?.message || "Order delete request rejected successfully!",
        "success"
      );
      fetchPendingDeleteRequests();
    } catch (error) {
      console.error("Reject Delete Error:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to reject delete request!",
        "error"
      );
    } finally {
      setRejectDialogOpen(false);
      setDeleteId(null);
      setSelectedRow(null);
      setRejectionReason("");
    }
  };

  /** Columns for DataGrid */
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      filterable: false,
      minWidth: isMobile ? 80 : 120,
      renderCell: (params) => (
        <Box>
          {/* Approve Button */}
          <IconButton
            onClick={() => {
              setDeleteId(params.row.id);
              setSelectedRow(params.row);
              setApproveDialogOpen(true);
            }}
            sx={{
              backgroundColor: "rgba(0,128,0,0.1)",
              borderRadius: 1.5,
              padding: isMobile ? "10px" : "8px",
              mr: 1,
              "&:hover": { backgroundColor: "rgba(0,128,0,0.2)" },
            }}
          >
            <CheckIcon sx={{ fontSize: isMobile ? 18 : 16, color: "green" }} />
          </IconButton>

          {/* Reject Button */}
          <IconButton
            onClick={() => {
              setDeleteId(params.row.id);
              setSelectedRow(params.row);
              setRejectDialogOpen(true);
            }}
            sx={{
              backgroundColor: "rgba(255,0,0,0.1)",
              borderRadius: 1.5,
              padding: isMobile ? "10px" : "8px",
              "&:hover": { backgroundColor: "rgba(255,0,0,0.2)" },
            }}
          >
            <ClearIcon sx={{ fontSize: isMobile ? 18 : 16, color: "red" }} />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "order_number",
      headerName: "Order No",
      flex: 1,
      minWidth: isMobile ? 100 : 150,
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1,
      minWidth: isMobile ? 120 : 150,
    },
    { field: "branchName", headerName: "Branch", flex: 1.3, minWidth: 160 },
    { field: "order_date", headerName: "Order Date", flex: 1, minWidth: 180 },
    {
      field: "total_amount",
      headerName: "Total",
      flex: 0.8,
      minWidth: isMobile ? 80 : 120,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: isMobile ? 140 : 120,
      renderCell: (params) => {
        const status = params.value;
        const { backgroundColor, color } = statusColors[status] || {
          backgroundColor: "#E0E0E0",
          color: "#000000",
        };
        return (
          <Chip
            label="Pending Delete Approval"
            sx={{
              backgroundColor,
              color,
              fontSize: isMobile ? "0.75rem" : "0.85rem",
              fontWeight: 500,
              borderRadius: "12px",
            }}
          />
        );
      },
    },
  ];

  return (
    <Box mb={{ xs: 0, sm: 1 }} sx={{ padding: isMobile ? 1 : 0 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Pending Delete Requests
      </Typography>

      {/* DataGrid */}
      <DataGrid
        rows={rows}
        columns={columns}
        rowHeight={isMobile ? 48 : 56}
        loading={loading}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        sx={{
          fontFamily: '"Poppins", sans-serif',
          borderRadius: "10px",
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: isMobile ? "14px" : "18px",
            fontWeight: 500,
            pl: 1,
          },
          "& .MuiDataGrid-cell": {
            fontSize: isMobile ? "12px" : "14px",
            pl: 2,
          },
          "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#fff" },
          "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#f4f4f4" },
          "& .MuiLinearProgress-bar": { backgroundColor: Color.primary.main },
          "& .MuiLinearProgress-colorPrimary": {
            backgroundColor: Color.secondary.main,
          },
          overflowX: isMobile ? "hidden" : "auto",
        }}
      />

      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { width: isMobile ? "90vw" : "auto", m: isMobile ? 1 : 2 },
        }}
      >
        <DialogTitle>Approve Delete Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve the deletion of the order{" "}
            <strong>{selectedRow?.order_number}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={() => setApproveDialogOpen(false)}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleApproveDelete(deleteId)}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              "&:hover": { background: Color.primary.dark },
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { width: isMobile ? "90vw" : "auto", m: isMobile ? 1 : 2 },
        }}
      >
        <DialogTitle>Reject Delete Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject the deletion of the order{" "}
            <strong>{selectedRow?.order_number}</strong>?
          </Typography>
          <TextField
            fullWidth
            label="Rejection Reason (Optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={() => setRejectDialogOpen(false)}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleRejectDelete(deleteId)}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              "&:hover": { background: Color.primary.dark },
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderApproval;
